import clsx from 'clsx';
import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { BookAnotherProviderArrowButton } from '@provider-directory/components/availability/buttons/book-another-provider-arrow-button.component';
import { BookAnotherProviderButton } from '@provider-directory/components/availability/buttons/book-another-provider-button.component';
import { CallToInquireButton } from '@provider-directory/components/availability/buttons/call-to-inquire-button.component';
import { useOrgContext } from '@provider-directory/components/org-context/org-context-provider';
import { useFeatureFlags } from '@provider-directory/contexts/FlagContext';
import { cn } from '@provider-directory/functions/cn';
import { getAvailablePhoneNumber } from '@provider-directory/functions/get-available-phone';
import { useIsDesktop } from '@provider-directory/hooks/useBreakpoints';
import { SchedulingUIMode } from '@provider-directory/hooks/useSpecialityScheduling';

import type { FeatureFlags } from '@provider-directory/types/flags';
import type {
  ProviderLocationResource,
  ProviderResource,
  ProvidersSearchCriteria,
} from '@provider-directory/types/providers.contracts';
import { ConversionContext } from '../../lib/analytics/conversions';

interface NoSlotsActionsProps
  extends Pick<ConversionContext, 'linkContextPage' | 'linkContextComponent'> {
  provider: ProviderResource;
  location: ProviderLocationResource;
  schedulingBehavior: SchedulingUIMode;
  searchCriteria?: Partial<ProvidersSearchCriteria>;
  onOpenDecisionTree?: () => void;
  showBookAnotherProviderButton?: boolean;
}

const isBookAnotherProviderVisible = (
  featureFlags: FeatureFlags,
  schedulingBehavior: SchedulingUIMode,
  onOpenDecisionTree: (() => void) | undefined,
  searchCriteria: Partial<ProvidersSearchCriteria> | undefined,
): boolean => {
  return !!(
    featureFlags.isBookAnotherProviderEnabled &&
    schedulingBehavior === 'call+book' &&
    onOpenDecisionTree &&
    !searchCriteria?.['reason-for-visit']
  );
};

export const NoSlotsSearchDesktop = ({
  provider,
  location,
  linkContextPage,
  linkContextComponent,
  searchCriteria,
  schedulingBehavior,
  onOpenDecisionTree,
  showBookAnotherProviderButton = true,
}: NoSlotsActionsProps): ReactElement => {
  const { featureFlags } = useFeatureFlags();
  const orgContext = useOrgContext();

  const phone = getAvailablePhoneNumber(
    location,
    provider,
    orgContext.getAssociationPhoneSelection(),
  );

  const showBookAnotherProvider =
    showBookAnotherProviderButton &&
    isBookAnotherProviderVisible(
      featureFlags,
      schedulingBehavior,
      onOpenDecisionTree,
      searchCriteria,
    );

  return (
    <div className={clsx('flex w-full flex-col')}>
      <div
        className={
          'my-auto flex w-full flex-col items-center justify-center gap-4'
        }
      >
        <CallToInquireButton
          searchCriteria={searchCriteria}
          provider={provider}
          phoneNumber={phone || ''}
          linkContextPage={linkContextPage}
          linkContextComponent={linkContextComponent}
          variant={'clientTonal'}
          className={clsx(
            'w-full !bg-primary-100',
            '!text-body-broad-small !h-[unset] !rounded-sm !p-5 !py-5 !font-medium',
          )}
        />
        {showBookAnotherProvider && (
          <BookAnotherProviderArrowButton
            provider={provider}
            onOpenDecisionTree={onOpenDecisionTree}
            searchCriteria={searchCriteria}
            linkContextPage={linkContextPage}
            linkContextComponent={linkContextComponent}
          />
        )}
      </div>
    </div>
  );
};

export const NoSlotsSearchMobile = ({
  provider,
  location,
  linkContextPage,
  linkContextComponent,
  searchCriteria,
  schedulingBehavior,
  onOpenDecisionTree,
  showBookAnotherProviderButton = true,
}: NoSlotsActionsProps): ReactElement => {
  const { featureFlags } = useFeatureFlags();
  const orgContext = useOrgContext();
  const phoneNumber = getAvailablePhoneNumber(
    location,
    provider,
    orgContext.getAssociationPhoneSelection(),
  );
  const showBookAnotherProvider =
    showBookAnotherProviderButton &&
    isBookAnotherProviderVisible(
      featureFlags,
      schedulingBehavior,
      onOpenDecisionTree,
      searchCriteria,
    );

  return (
    <div className={'flex h-full w-full flex-col justify-center gap-2'}>
      <CallToInquireButton
        provider={provider}
        phoneNumber={phoneNumber}
        className={'!h-auto !leading-[140%]'} // At small viewports, the text is too long for the Fab button height
        variant={'clientTonal'}
        searchCriteria={searchCriteria}
        linkContextPage={linkContextPage}
        linkContextComponent={linkContextComponent}
      />
      {showBookAnotherProvider && (
        <BookAnotherProviderButton
          provider={provider}
          onOpenDecisionTree={onOpenDecisionTree}
          searchCriteria={searchCriteria}
          linkContextPage={linkContextPage}
          linkContextComponent={linkContextComponent}
        />
      )}
    </div>
  );
};

export const NoSlotsDetails = ({
  provider,
  location,
  schedulingBehavior,
  searchCriteria,
  onOpenDecisionTree,
  linkContextPage,
  linkContextComponent,
}: NoSlotsActionsProps): ReactElement => {
  const isDesktop = useIsDesktop();
  const { featureFlags } = useFeatureFlags();
  const orgContext = useOrgContext();
  const { t } = useTranslation();
  const phoneNumber = getAvailablePhoneNumber(
    location,
    provider,
    orgContext.getAssociationPhoneSelection(),
  );
  const showBookAnotherProvider = isBookAnotherProviderVisible(
    featureFlags,
    schedulingBehavior,
    onOpenDecisionTree,
    searchCriteria,
  );

  return (
    <div className={'flex h-full w-full flex-col justify-center'}>
      {isDesktop && (
        <p
          className={cn('pb-6 text-neutral-700')}
          data-testid={'no-slots-actions-details-text'}
        >
          {t('search.providerNotAvailableOnline')}
        </p>
      )}
      <CallToInquireButton
        provider={provider}
        phoneNumber={phoneNumber}
        className={'mb-2'}
        variant={'clientMain'}
        searchCriteria={searchCriteria}
        linkContextPage={linkContextPage}
        linkContextComponent={linkContextComponent}
      />
      {showBookAnotherProvider && (
        <BookAnotherProviderButton
          provider={provider}
          onOpenDecisionTree={onOpenDecisionTree}
          searchCriteria={searchCriteria}
          linkContextPage={linkContextPage}
          linkContextComponent={linkContextComponent}
        />
      )}
    </div>
  );
};
